import React, { useEffect, useState } from 'react';
import { AdminCheckbox, AdminInput, AdminSelect } from '@project/components';
import { Button, Form } from 'react-bootstrap';
import { titleize } from '../../utils/utils';
import { useIntl } from 'react-intl';

export default function CompanyIntegrationsTab({ company, onSaveCompanySettings, companySettings, isSaving }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const [integrationSettings, setIntegrationSettings] = useState(companySettings.integrationSettings);

  // set the init state for integration settings here.
  useEffect(() => {
    setIntegrationSettings(companySettings.integrationSettings);
  }, [companySettings]);

  const updateField = (value, base, field) => {
    base[field] = value;
    setIntegrationSettings({ ...integrationSettings });
  }

  const renderField = (base, key, options = {}) => {
    const labels = {
      'authUsername': t('CompanyIntegrationsTabs.authUsername'),
      'authPassword': t('CompanyIntegrationsTabs.authPassword'),
      'authEndpoint': t('CompanyIntegrationsTabs.authEndpoint'),
      'authTokenKey': t('CompanyIntegrationsTabs.authTokenKey'),
      'authHeaderKey': t('CompanyIntegrationsTabs.authHeaderKey'),
      'endpoint': t('CompanyIntegrationsTabs.endpoint'),
      'headers': t('CompanyIntegrationsTabs.headers'),
      'payload': t('CompanyIntegrationsTabs.payload'),
      'authClientID': t('CompanyIntegrationsTabs.authClientID'),
      'authClientSecret': t('CompanyIntegrationsTabs.authClientSecret'),
      'authScope': t('CompanyIntegrationsTabs.authScope')
    }
    return (
      <AdminInput
        id={labels[key]}
        label={options.label || labels[key] || titleize(key)}
        value={base[key] || ''}
        type={options.type || 'text'}
        onChange={(e) => updateField(e.target.value, base, key)} />)
  }

  const authTypeOptions = [
    { label: 'Standard', value: 'standard' },
    { label: 'OAuth2.0 - Client Credentials', value: 'oauth2-client' }
  ];

  const payloadContentTypeOptions = [
    { label: 'JSON', value: 'application/json' },
    { label: 'URL-Encoded Form', value: 'application/x-www-form-urlencoded' }
  ];

  if (!integrationSettings) return null;

  const { publish } = integrationSettings;

  return (
    <div>
      <div className='row my-sm-5'>
        <div className="font-lg col-sm-8" style={{ lineHeight: '40px' }}>{t('CompanyIntegrationsTabs.description')}</div>
        <div className="col-sm-4" align="right">
          <Button disabled={isSaving} onClick={() => onSaveCompanySettings({ integrationSettings })} >
            {t('CompanyIntegrationsTabs.saveButton')}
          </Button>
        </div>
      </div>
      <Form key="field_visibility_form" className="is-common-form">
        <h2 className='is-main-heading pb-3'>{t('CompanyIntegrationsTabs.title')}</h2>
        <div className='row'>
          <div className="col-sm-6">
            <AdminCheckbox
              id="publishEnabled"
              label={t('CompanyIntegrationsTabs.enablePublish')}
              checked={!!publish.enabled}
              onChange={(e) => updateField(e.target.checked, publish, 'enabled')} />
          </div>
        </div>
        <div className='row mb-sm-5'>
          <div className="col-sm-6">
            <AdminCheckbox
              id="authRequired"
              label={t('CompanyIntegrationsTabs.authRequired')}
              checked={!!publish.authRequired}
              onChange={(e) => updateField(e.target.checked, publish, 'authRequired')} />
          </div>
        </div>
        {publish && publish.authRequired &&
          <div>
            <AdminSelect
              label={t('CompanyIntegrationsTabs.authType.label')}
              placeholder={t('CompanyIntegrationsTabs.authType.placeholder')}
              value={authTypeOptions.find(_ => publish.authType === _.value) || authTypeOptions.find(_ => _.value === 'standard')}
              onChange={(e) => updateField(e.value, publish, 'authType')}
              options={authTypeOptions}
            />
          </div>
        }
        {publish.authRequired && (!publish.authType || publish.authType === 'standard') ?
          <>
            <div className='row'>
              <div className="col-sm-6">
                {renderField(publish, 'authUsername')}
              </div>
              <div className="col-sm-6">
                {renderField(publish, 'authPassword', { type: 'password' })}
              </div>
            </div>
            <div className='row'>
              <div className="col-sm-6">
                {renderField(publish, 'authEndpoint')}
              </div>
              <div className="col-sm-6">
                {renderField(publish, 'authTokenKey')}
              </div>
            </div>
            <div className='row'>
              <div className="col-sm-6">
                {renderField(publish, 'authHeaderKey')}
              </div>
              <div className="col-sm-6">
                <AdminCheckbox
                  id="isBearerAuth"
                  label={t('CompanyIntegrationsTabs.bearerToken')}
                  checked={!!publish.isBearerAuth}
                  onChange={(e) => updateField(e.target.checked, publish, 'isBearerAuth')} />
              </div>
            </div>
          </>
          : null}
        {publish.authRequired && publish.authType === 'oauth2-client' &&
          <>
            <div className='row'>
              <div className="col-sm-6">
                {renderField(publish, 'authClientID')}
              </div>
              <div className="col-sm-6">
                {renderField(publish, 'authClientSecret', { type: 'password' })}
              </div>
            </div>
            <div className='row'>
              <div className="col-sm-6">
                {renderField(publish, 'authScope')}
              </div>
              <div className="col-sm-6">
                {renderField(publish, 'authEndpoint')}
              </div>
            </div>
            <div className='row'>
              <div className="col-sm-6">
                {renderField(publish, 'authTokenKey')}
              </div>
              <div className="col-sm-6">
                {renderField(publish, 'authHeaderKey')}
              </div>
            </div>
            <div className='row'>
              <div className="col-sm-6">
                <AdminCheckbox
                  id="isBearerAuth"
                  label={t('CompanyIntegrationsTabs.bearerToken')}
                  checked={!!publish.isBearerAuth}
                  onChange={(e) => updateField(e.target.checked, publish, 'isBearerAuth')} />
              </div>
            </div>
          </>
        }
        <div className='row my-sm-5'>
          <div className="col-sm-12">
            {renderField(publish, 'endpoint')}
          </div>
        </div>
        <div className='row mt-sm-5'>
          <div className="col-sm-6">
            <AdminSelect
              id="payloadFormat"
              label={t('CompanyIntegrationsTabs.payloadFormat.label')}
              placeholder={t('CompanyIntegrationsTabs.payloadFormat.placeholder')}
              value={payloadContentTypeOptions.find(_ => publish.payloadContentType === _.value) || payloadContentTypeOptions.find(_ => _.value === 'application/json')}
              onChange={(e) => updateField(e.value, publish, 'payloadContentType')}
              options={payloadContentTypeOptions}
            />
          </div>
        </div>
        <div className='row mb-sm-5'>
          <div className="col-sm-12">
            {renderField(publish, 'payload', { type: 'textarea' })}
          </div>
        </div>
        <div className='row my-sm-5'>
          <div className="col-sm-12">
            {renderField(publish, 'headers', { type: 'textarea' })}
          </div>
        </div>

      </Form>
    </div>
  )
}
